import Image from 'next/image'

import { cn, formatPrice, orderFlowPriceFormatOptions } from '@/_lib/utils'

import { AOrderDoctorBlock } from '../../a-order-doctor-block'

/** Layout density for cart rows shared by sidebar and mobile header. */
export type CartVariant = 'sidebar' | 'compact'

/**
 * Title + price row; compact wraps, sidebar is a single baseline row with optional struck-through old price.
 *
 * @param pricePrefix - Optional text prepended before the formatted price (e.g. "od ").
 *   Used in the mobile header / compact cart when no insurer has been selected yet
 *   and we show the minimum "from" price.
 */
export function ProductNameRow({
	name,
	price,
	oldPrice,
	pricePrefix,
	variant
}: {
	name: string
	price: number | null
	oldPrice?: number | null
	/** Text prepended before the formatted price, e.g. "od ". */
	pricePrefix?: string
	variant: CartVariant
}) {
	const f = formatPrice(price, orderFlowPriceFormatOptions)
	const fOld = oldPrice != null ? formatPrice(oldPrice, orderFlowPriceFormatOptions) : null

	if (variant === 'compact') {
		return (
			<div className='flex flex-wrap items-baseline gap-x-2'>
				<p className='min-w-0 flex-1 line-clamp-2 text-xs font-medium leading-snug text-medante-purple-dark'>
					{name}
				</p>
				<div className={cn('flex shrink-0 items-baseline gap-1.5')}>
					<p className='text-xs font-semibold text-medante-purple-dark'>
						{pricePrefix}{f.price}&nbsp;{f.currency}
					</p>
					{fOld && (
						<p className='text-xs text-medante-purple-dark/40 line-through'>
							{fOld.price}&nbsp;{fOld.currency}
						</p>
					)}
				</div>
			</div>
		)
	}

	return (
		<div className='flex items-baseline justify-between gap-4'>
			<p className='min-w-0 flex-1 font-semibold leading-snug text-medante-purple-dark'>
				{name}
			</p>
			<div className='flex shrink-0 items-baseline gap-2'>
				<p className='font-semibold text-medante-purple-dark'>
					{pricePrefix}{f.price}&nbsp;{f.currency}
				</p>
				{fOld && (
					<p className='text-sm text-medante-purple-dark/40 line-through'>
						{fOld.price}&nbsp;{fOld.currency}
					</p>
				)}
			</div>
		</div>
	)
}

/** One extra paid module line (`+` prefix). */
export function ModuleRow({
	name,
	price,
	variant
}: {
	name: string
	price: number
	variant: CartVariant
}) {
	const f = formatPrice(price, orderFlowPriceFormatOptions)

	if (variant === 'compact') {
		return (
			<li className='flex items-baseline gap-x-1.5'>
				<span
					className='shrink-0 text-xs font-medium text-medante-purple-dark/40 select-none'
					aria-hidden
				>
					+
				</span>
				<span className='min-w-0 flex-1 truncate text-xs text-medante-purple-dark/60'>
					{name}
				</span>
				<span className='shrink-0 text-xs text-medante-purple-dark/60'>
					{f.price}&nbsp;{f.currency}
				</span>
			</li>
		)
	}

	return (
		<li className='flex items-baseline gap-x-2'>
			<span
				className='shrink-0 text-xs md:text-sm font-medium text-medante-purple-dark/40 select-none'
				aria-hidden
			>
				+
			</span>
			<span className='min-w-0 flex-1 text-xs md:text-sm text-medante-purple-dark/80'>
				{name}
			</span>
			<span className='shrink-0 text-sm text-medante-purple-dark/80'>
				{f.price}&nbsp;{f.currency}
			</span>
		</li>
	)
}

/** Pink discount line (label + amount). */
export function DiscountRow({
	label,
	amount,
	variant
}: {
	label: string
	amount: number
	variant: CartVariant
}) {
	const f = formatPrice(amount, orderFlowPriceFormatOptions)
	const textSize = variant === 'compact' ? 'text-xs' : 'text-sm'

	return (
		<div className='flex items-baseline justify-between gap-4'>
			<p className={`${textSize} font-medium text-medante-pink-secondary`}>{label}</p>
			<p className={`${textSize} font-medium text-medante-pink-secondary`}>
				{f.price}&nbsp;{f.currency}
			</p>
		</div>
	)
}

/** Subtotal label used on some legacy line layouts. */
export function ProductTotalRow({ amount, variant }: { amount: number; variant: CartVariant }) {
	const f = formatPrice(amount, orderFlowPriceFormatOptions)
	const textSize = variant === 'compact' ? 'text-xs' : ''

	return (
		<div className='flex items-baseline justify-between gap-4'>
			<p className={`${textSize} font-semibold text-medante-purple-dark`}>Cena spolu</p>
			<p className={`${textSize} font-semibold text-medante-purple-dark`}>
				{f.price}&nbsp;{f.currency}
			</p>
		</div>
	)
}

/** Small doctor strip under a template line (photo + name + optional meta). */
export function DoctorCard({
	photo,
	name,
	position,
	ageLabel,
	languagesLabel,
	variant
}: {
	photo?: string | null
	name: string
	position?: string | null
	ageLabel?: string
	languagesLabel?: string | null
	variant: CartVariant
}) {
	if (variant === 'compact') {
		return (
			<div className='mt-1 flex items-center gap-2 border-t border-medante-purple-dark/10 pt-1.5'>
				{photo ? (
					<Image
						src={photo}
						alt={name}
						width={20}
						height={20}
						unoptimized
						className='h-5 w-5 shrink-0 rounded-full object-cover'
					/>
				) : (
					<div className='h-5 w-5 shrink-0 rounded-full bg-medante-purple' />
				)}
				<p className='truncate text-xs text-medante-purple-dark/60'>{name}</p>
			</div>
		)
	}

	return (
		<AOrderDoctorBlock
			photoUrl={photo ?? null}
			fullName={name}
			position={position ?? null}
			ageLabel={ageLabel ?? ''}
			languagesLabel={languagesLabel ?? null}
			className='mt-3 md:mt-4'
		/>
	)
}

/** Pulse placeholder while a cart line fetches pricing. */
export function CartSkeleton({ variant }: { variant: CartVariant }) {
	if (variant === 'compact') {
		return (
			<div className='animate-pulse space-y-1.5'>
				<div className='h-3 w-2/3 rounded bg-medante-purple-dark/10' />
				<div className='h-3 w-1/3 rounded bg-medante-purple-dark/10' />
			</div>
		)
	}

	return (
		<div className='animate-pulse space-y-2'>
			<div className='h-4 w-3/4 rounded bg-medante-purple-dark/10' />
			<div className='h-3 w-1/2 rounded bg-medante-purple-dark/10' />
			<div className='h-4 w-2/3 rounded bg-medante-purple-dark/10' />
		</div>
	)
}
